import { useEffect, useRef, useState } from "react";

/**
 * Counts from 0 to `target` the first time the element scrolls into view.
 * Returns { ref, value } — value is a number the component formats. Reduced
 * motion jumps straight to the target.
 */
const useCountUp = (target, { duration = 1200 } = {}) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const node = ref.current;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!node || reduce || typeof IntersectionObserver === "undefined") {
      setValue(target);
      return undefined;
    }

    let started = false;
    let raf = 0;

    const run = () => {
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setValue(target % 1 ? +(target * eased).toFixed(1) : Math.round(target * eased));
        if (p < 1) {
          raf = window.requestAnimationFrame(tick);
        }
      };
      raf = window.requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          started = true;
          run();
          io.unobserve(node);
        }
      },
      { threshold: 0.5 }
    );
    io.observe(node);

    return () => {
      io.disconnect();
      window.cancelAnimationFrame(raf);
    };
  }, [target, duration]);

  return { ref, value };
};

export default useCountUp;
